<template>
  <div class="wtp" ref="root">

    <div class="wtp-display"
         :class="{ 'wtp-open': open, 'wtp-disabled': disabled }"
         @click="onDisplayClick">
      <i class="fa-regular fa-clock wtp-clock"></i>
      <input class="wtp-input"
             ref="input"
             type="text"
             inputmode="numeric"
             autocomplete="off"
             :readonly="touchMode || disabled"
             :placeholder="placeholder"
             :value="editing ? editText : displayValue"
             @focus="onInputFocus"
             @input="onInputType"
             @keydown.enter.prevent="onInputEnter"
             @keydown.esc="onInputEsc"
             @blur="onInputBlur">
      <i v-if="clearable && displayValue && !disabled"
         class="fa-solid fa-xmark wtp-clear"
         @mousedown.prevent
         @click.stop="clear"></i>
    </div>

    <div v-if="open"
         class="wtp-panel"
         :style="panelStyle"
         dir="ltr">
      <div class="wtp-highlight"></div>
      <div class="wtp-fade wtp-fade-top"></div>
      <div class="wtp-fade wtp-fade-bottom"></div>

      <div class="wtp-wheels"
           @wheel.passive="markInteracted"
           @touchstart.passive="markInteracted"
           @mousedown="markInteracted">

        <div class="wtp-wheel" ref="hourWheel"
             @scroll.passive="onScroll"
             @pointerdown="onWheelPointerDown('hour', $event)">
          <div class="wtp-pad"></div>
          <div v-for="(h, i) in hours"
               :key="'h' + h"
               class="wtp-item"
               :class="itemClass(i, hourIndex)"
               @click="pick('hour', i)">{{ h }}</div>
          <div class="wtp-pad"></div>
        </div>

        <div class="wtp-colon">:</div>

        <div class="wtp-wheel" ref="minuteWheel"
             @scroll.passive="onScroll"
             @pointerdown="onWheelPointerDown('minute', $event)">
          <div class="wtp-pad"></div>
          <div v-for="(m, i) in minutes"
               :key="'m' + m"
               class="wtp-item"
               :class="itemClass(i, minuteIndex)"
               @click="pick('minute', i)">{{ m }}</div>
          <div class="wtp-pad"></div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
const ITEM_H = 40;      // row height (px)
const VISIBLE = 5;      // visible rows
const PANEL_W = 220;
const PANEL_H = ITEM_H * VISIBLE + 2; // + borders

function pad2(n) {
  return n.toString().padStart(2, '0');
}

export default {
  name: 'WheelTimePicker',
  props: {
    // "HH:MM" string (accepts non-padded "9:30" too)
    value: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '--:--',
    },
    minuteStep: {
      type: Number,
      default: 5,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      hourIndex: 0,
      minuteIndex: 0,
      panelStyle: {},
      interacted: false, // don't emit until the user actually scrolls/clicks
      settleTimer: null,
      lastEmitted: null,
      repositionRaf: null,
      editing: false,
      editText: '',
      // on touch devices the wheel is the input; keep the field readonly so
      // tapping it doesn't pop the OS keyboard over the wheel
      touchMode: typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(pointer: coarse)').matches
        : false,
    };
  },
  computed: {
    hours() {
      const res = [];
      for (let i = 0; i < 24; i++) res.push(pad2(i));
      return res;
    },
    minutes() {
      const res = [];
      for (let i = 0; i < 60; i += this.minuteStep) res.push(pad2(i));
      return res;
    },
    displayValue() {
      const parsed = this.parse(this.value);
      if (!parsed) return '';
      return pad2(parsed.h) + ':' + pad2(parsed.m);
    },
  },
  watch: {
    value(v) {
      // External change while open (form reset etc.) -> re-align wheels
      if (this.open && v !== this.lastEmitted) {
        this.interacted = false; // realign scroll must not emit over the external value
        this.alignWheels(false);
      }
    },
  },
  created() {
    // non-reactive drag bookkeeping (no template bindings -> keep out of data)
    this.dragState = null;
    this.ignoreNextItemClick = false;
    this.snapRestoreTimer = null;
  },
  beforeDestroy() {
    this.unbindGlobal();
    clearTimeout(this.settleTimer);
    clearTimeout(this.snapRestoreTimer);
    if (this.repositionRaf) cancelAnimationFrame(this.repositionRaf);
    window.removeEventListener('pointermove', this.onWheelPointerMove);
    window.removeEventListener('pointerup', this.onWheelPointerUp);
    window.removeEventListener('pointercancel', this.onWheelPointerUp);
  },
  methods: {
    parse(v) {
      if (!v || typeof v !== 'string') return null;
      const m = v.match(/^\s*(\d{1,2}):(\d{1,2})/);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      let min = parseInt(m[2], 10);
      if (isNaN(h) || isNaN(min)) return null;
      h = Math.min(23, Math.max(0, h));
      min = Math.min(59, Math.max(0, min));
      return { h: h, m: min };
    },

    onDisplayClick() {
      if (this.disabled) return;
      if (!this.open) this.openPanel();
      if (this.$refs.input && !this.touchMode) this.$refs.input.focus();
    },

    // --- typed input ---
    onInputFocus() {
      if (this.disabled) return;
      this.editing = true;
      this.editText = this.displayValue;
      if (!this.open) this.openPanel();
    },

    onInputType(e) {
      this.editText = e.target.value;
      const p = this.parseLoose(this.editText);
      if (p && this.open) this.previewAlign(p);
    },

    onInputEnter() {
      this.commitTyped();
      this.close();
      if (this.$refs.input) this.$refs.input.blur();
    },

    onInputEsc() {
      this.editText = this.displayValue; // cancel edit
      this.close();
      if (this.$refs.input) this.$refs.input.blur();
    },

    onInputBlur() {
      this.commitTyped();
      this.editing = false;
    },

    commitTyped() {
      if (!this.editing) return;
      const raw = this.editText;
      if (raw === this.displayValue) return; // nothing typed
      if (!raw) return; // emptying commits nothing; clearing is the x button
      const p = this.parseLoose(raw);
      if (!p) {
        this.editText = this.displayValue; // revert garbage
        return;
      }
      const val = pad2(p.h) + ':' + pad2(p.m);
      this.editText = val;
      if (val !== this.value) {
        this.lastEmitted = val;
        this.$emit('input', val);
        this.$emit('change', val);
      }
    },

    // Forgiving time parse for typing: "19:30", "19:3" (->19:30), "1930",
    // "930" (->09:30), "9" (->09:00)
    parseLoose(str) {
      if (!str) return null;
      const cleaned = str.replace(/[^\d:]/g, '');
      if (!cleaned) return null;
      let h, m;
      if (cleaned.indexOf(':') >= 0) {
        const parts = cleaned.split(':');
        h = parseInt(parts[0], 10);
        m = parseInt(parts[1], 10);
        if (isNaN(m)) m = 0;
        else if (parts[1].length === 1) m = m * 10; // "19:3" means 19:30
      } else if (cleaned.length <= 2) {
        h = parseInt(cleaned, 10);
        m = 0;
      } else if (cleaned.length === 3) {
        h = parseInt(cleaned.slice(0, 1), 10);
        m = parseInt(cleaned.slice(1), 10);
      } else {
        h = parseInt(cleaned.slice(0, 2), 10);
        m = parseInt(cleaned.slice(2, 4), 10);
      }
      if (isNaN(h)) return null;
      if (isNaN(m)) m = 0;
      h = Math.min(23, Math.max(0, h));
      m = Math.min(59, Math.max(0, m));
      if (this.minuteStep > 1) {
        m = Math.round(m / this.minuteStep) * this.minuteStep;
        if (m >= 60) { m = 0; h = (h + 1) % 24; }
      }
      return { h: h, m: m };
    },

    // Follow the typed text with the wheels, without emitting
    previewAlign(p) {
      this.interacted = false; // the preview scroll must not settle-emit
      this.hourIndex = p.h;
      this.minuteIndex = Math.min(this.minutes.length - 1, Math.round(p.m / this.minuteStep));
      const hw = this.$refs.hourWheel;
      const mw = this.$refs.minuteWheel;
      if (hw) hw.scrollTop = this.hourIndex * ITEM_H;
      if (mw) mw.scrollTop = this.minuteIndex * ITEM_H;
    },

    computePanelStyle() {
      const rect = this.$refs.root.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow > PANEL_H + 14
        ? rect.bottom + 6
        : rect.top - PANEL_H - 6;
      let left = rect.left + rect.width / 2 - PANEL_W / 2;
      left = Math.min(Math.max(8, left), window.innerWidth - PANEL_W - 8);
      return {
        top: top + 'px',
        left: left + 'px',
        width: PANEL_W + 'px',
      };
    },

    openPanel() {
      this.panelStyle = this.computePanelStyle();
      this.interacted = false;
      this.open = true;
      this.$nextTick(() => {
        this.alignWheels(true);
        this.bindGlobal();
      });
    },

    // The panel is position:fixed, so outside scrolls / resizes move the anchor
    // out from under it -> follow the anchor instead of closing
    reposition() {
      if (this.repositionRaf) return;
      this.repositionRaf = requestAnimationFrame(() => {
        this.repositionRaf = null;
        if (!this.open) return;
        this.panelStyle = this.computePanelStyle();
      });
    },

    close() {
      if (!this.open) return;
      clearTimeout(this.settleTimer);
      this.onSettle(); // flush a pending pick so fast outside-clicks don't lose it
      this.open = false;
      this.unbindGlobal();
    },

    clear() {
      this.interacted = false; // don't let close() re-commit the wheel position
      this.close();
      this.lastEmitted = null;
      this.editText = '';
      this.$emit('input', null);
    },

    // scrollTo({behavior:'smooth'}) silently never starts in background or
    // headless windows -- if nothing moved shortly after, land instantly
    smoothScrollTo(wheel, top) {
      const from = wheel.scrollTop;
      if (from === top) return;
      wheel.scrollTo({ top: top, behavior: 'smooth' });
      setTimeout(function () {
        if (wheel.scrollTop === from) wheel.scrollTop = top;
      }, 250);
    },

    // Position wheels on the current value (or now, when empty)
    alignWheels(instant) {
      const parsed = this.parse(this.value) || this.defaultTime();
      this.hourIndex = parsed.h;
      this.minuteIndex = Math.min(
        this.minutes.length - 1,
        Math.round(parsed.m / this.minuteStep)
      );
      const hw = this.$refs.hourWheel;
      const mw = this.$refs.minuteWheel;
      if (!hw || !mw) return;
      if (instant) {
        hw.scrollTop = this.hourIndex * ITEM_H;
        mw.scrollTop = this.minuteIndex * ITEM_H;
      } else {
        this.smoothScrollTo(hw, this.hourIndex * ITEM_H);
        this.smoothScrollTo(mw, this.minuteIndex * ITEM_H);
      }
    },

    defaultTime() {
      const now = new Date();
      let m = Math.round(now.getMinutes() / this.minuteStep) * this.minuteStep;
      let h = now.getHours();
      if (m >= 60) { m = 0; h = (h + 1) % 24; }
      return { h: h, m: m };
    },

    markInteracted() {
      this.interacted = true;
    },

    onScroll() {
      const hw = this.$refs.hourWheel;
      const mw = this.$refs.minuteWheel;
      if (!hw || !mw) return;
      this.hourIndex = this.clampIndex(hw.scrollTop, this.hours.length);
      this.minuteIndex = this.clampIndex(mw.scrollTop, this.minutes.length);
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 160);
    },

    clampIndex(scrollTop, len) {
      return Math.min(len - 1, Math.max(0, Math.round(scrollTop / ITEM_H)));
    },

    onSettle() {
      if (!this.open || !this.interacted) return;
      this.commitIndices(this.hourIndex, this.minuteIndex);
    },

    commitIndices(hi, mi) {
      this.hourIndex = hi;
      this.minuteIndex = mi;
      const val = this.hours[hi] + ':' + this.minutes[mi];
      if (this.editing) this.editText = val; // wheel wins over a stale typed text
      if (val === this.value) return;
      this.lastEmitted = val;
      this.$emit('input', val);
      this.$emit('change', val);
    },

    pick(type, index) {
      if (this.ignoreNextItemClick) {
        // this "click" is just the tail of a mouse drag
        this.ignoreNextItemClick = false;
        return;
      }
      this.interacted = true;
      const wheel = type === 'hour' ? this.$refs.hourWheel : this.$refs.minuteWheel;
      if (!wheel) return;
      // commit directly -- scroll events are unreliable in background windows,
      // so the animation below is cosmetic only
      if (type === 'hour') this.commitIndices(index, this.minuteIndex);
      else this.commitIndices(this.hourIndex, index);
      this.smoothScrollTo(wheel, index * ITEM_H);
    },

    // --- mouse drag-to-scroll (desktop) ---
    // Touch already scrolls natively; this adds the same for a held mouse button.
    onWheelPointerDown(type, e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      const wheel = type === 'hour' ? this.$refs.hourWheel : this.$refs.minuteWheel;
      if (!wheel) return;
      e.preventDefault();
      this.interacted = true;
      clearTimeout(this.snapRestoreTimer);
      this.dragState = {
        wheel: wheel,
        startY: e.clientY,
        startTop: wheel.scrollTop,
        prevTop: wheel.scrollTop,
        prevT: e.timeStamp,
        lastTop: wheel.scrollTop,
        lastT: e.timeStamp,
        moved: false,
      };
      // native snap fights direct scrollTop writes -> disable while dragging
      wheel.classList.add('wtp-dragging');
      window.addEventListener('pointermove', this.onWheelPointerMove);
      window.addEventListener('pointerup', this.onWheelPointerUp);
      window.addEventListener('pointercancel', this.onWheelPointerUp);
    },

    onWheelPointerMove(e) {
      const d = this.dragState;
      if (!d) return;
      const dy = e.clientY - d.startY;
      if (Math.abs(dy) > 4) d.moved = true;
      d.prevTop = d.lastTop;
      d.prevT = d.lastT;
      d.wheel.scrollTop = d.startTop - dy;
      d.lastTop = d.wheel.scrollTop;
      d.lastT = e.timeStamp;
    },

    onWheelPointerUp() {
      const d = this.dragState;
      if (!d) return;
      this.dragState = null;
      window.removeEventListener('pointermove', this.onWheelPointerMove);
      window.removeEventListener('pointerup', this.onWheelPointerUp);
      window.removeEventListener('pointercancel', this.onWheelPointerUp);

      if (d.moved) {
        // the browser fires a click on the item under the cursor right after
        // pointerup -- swallow it so it doesn't re-scroll the wheel
        this.ignoreNextItemClick = true;
        setTimeout(this.clearIgnoreClick, 250);
      }

      // light momentum: project velocity ~160ms ahead, then land on a row
      const dt = d.lastT - d.prevT;
      const v = dt > 0 ? (d.lastTop - d.prevTop) / dt : 0;
      const fling = Math.max(-320, Math.min(320, v * 160));
      const isHour = d.wheel === this.$refs.hourWheel;
      const len = isHour ? this.hours.length : this.minutes.length;
      const idx = this.clampIndex(d.wheel.scrollTop + fling, len);
      if (isHour) this.commitIndices(idx, this.minuteIndex);
      else this.commitIndices(this.hourIndex, idx);
      this.smoothScrollTo(d.wheel, idx * ITEM_H);

      const wheel = d.wheel;
      this.snapRestoreTimer = setTimeout(function () {
        wheel.classList.remove('wtp-dragging');
      }, 450);
    },

    clearIgnoreClick() {
      this.ignoreNextItemClick = false;
    },

    itemClass(i, current) {
      const d = Math.abs(i - current);
      return {
        'wtp-sel': d === 0,
        'wtp-near': d === 1,
        'wtp-far': d >= 2,
      };
    },

    bindGlobal() {
      document.addEventListener('mousedown', this.onDocDown);
      document.addEventListener('touchstart', this.onDocDown, { passive: true });
      document.addEventListener('keydown', this.onDocKey);
      document.addEventListener('scroll', this.onAnyScroll, true);
      window.addEventListener('resize', this.reposition);
    },

    unbindGlobal() {
      document.removeEventListener('mousedown', this.onDocDown);
      document.removeEventListener('touchstart', this.onDocDown);
      document.removeEventListener('keydown', this.onDocKey);
      document.removeEventListener('scroll', this.onAnyScroll, true);
      window.removeEventListener('resize', this.reposition);
    },

    onDocDown(e) {
      if (this.$refs.root && this.$refs.root.contains(e.target)) return;
      this.close();
    },

    onDocKey(e) {
      if (e.key === 'Escape') this.close();
    },

    onAnyScroll(e) {
      if (e.target === this.$refs.hourWheel || e.target === this.$refs.minuteWheel) return;
      this.reposition();
    },
  },
};
</script>

<style scoped lang="scss">
.wtp {
  position: relative;
  min-width: 90px;
}

.wtp-display {
  display: flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #cfcfe0;
  }

  &.wtp-open,
  &:focus-within {
    border-color: #ab8ce4;
    box-shadow: 0 0 0 3px rgba(171, 140, 228, 0.15);
  }

  &.wtp-disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.wtp-clock {
  color: #b1aec4;
  font-size: 14px;
}

.wtp-input {
  flex: 1;
  min-width: 0;
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  text-align: center;
  direction: ltr;
  font-weight: 600;
  color: #262238;
  font-size: 15px;
  padding: 0;
  cursor: pointer;

  &::placeholder {
    color: #b1aec4;
    font-weight: 400;
    font-size: 14px;
  }

  &:focus {
    cursor: text;
  }

  &[readonly] {
    cursor: pointer;
  }
}

.wtp-clear {
  color: #b1aec4;
  font-size: 13px;
  padding: 3px;

  &:hover {
    color: #6c6a80;
  }
}

.wtp-panel {
  position: fixed;
  z-index: 3000;
  background: #fff;
  border: 1px solid #edecf5;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(28, 25, 58, 0.16), 0 2px 10px rgba(28, 25, 58, 0.08);
  overflow: hidden;
}

.wtp-wheels {
  position: relative;
  display: flex;
  justify-content: center;
  height: 200px;
}

.wtp-wheel {
  width: 78px;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  cursor: grab;

  &.wtp-dragging {
    scroll-snap-type: none;
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.wtp-pad {
  height: 80px; // (VISIBLE-1)/2 rows, lets first/last item reach center
}

.wtp-item {
  height: 40px;
  line-height: 40px;
  text-align: center;
  scroll-snap-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #262238;
  cursor: pointer;
  user-select: none;
  transition: color 0.15s, font-size 0.15s, opacity 0.15s;

  &.wtp-near {
    color: #bcbac9;
    font-size: 19px;
  }

  &.wtp-far {
    color: #dddce6;
    font-size: 17px;
  }
}

.wtp-colon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 200px;
  font-size: 20px;
  font-weight: 700;
  color: #262238;
  padding-bottom: 3px;
}

.wtp-highlight {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 40px;
  transform: translateY(-50%);
  border-top: 1px solid #efeef5;
  border-bottom: 1px solid #efeef5;
  pointer-events: none;
  z-index: 2;
}

.wtp-fade {
  position: absolute;
  left: 0;
  right: 0;
  height: 58px;
  pointer-events: none;
  z-index: 2;
}

.wtp-fade-top {
  top: 0;
  background: linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0));
}

.wtp-fade-bottom {
  bottom: 0;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.95));
}
</style>
